import Extent from '@arcgis/core/geometry/Extent.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import {
  ensureLayerOnMap,
  getMap,
  getMapView,
  setBasemapById,
  setLayerVisible,
} from '@/map/mapStore';
import { useBookmarkStore } from '@/state/bookmarkStore';
import { findLayer } from '@/map/layers/registry';
import type { Tool } from './types';

type Input = { id?: string; name?: string };

export const bookmarkGo: Tool<Input> = {
  name: 'bookmark_go',
  description:
    'Restore a saved bookmark — pans/zooms the view, applies basemap, toggles visible layers. Pass either id or name.',
  input_schema: {
    type: 'object',
    properties: {
      id: { type: 'string' },
      name: { type: 'string' },
    },
    additionalProperties: false,
  },
  execute: async ({ id, name }) => {
    if (!id && !name) throw new Error('bookmark.go: pass id or name');
    const list = useBookmarkStore.getState().bookmarks;
    const bookmark = id
      ? list.find((b) => b.id === id)
      : list.find((b) => b.name.toLowerCase() === (name ?? '').toLowerCase());
    if (!bookmark) throw new Error('bookmark.go: bookmark not found');

    const view = getMapView();
    const map = getMap();
    if (!view || !map) throw new Error('bookmark.go: map not initialised');

    setBasemapById(bookmark.basemap_id);

    const ensure = bookmark.visible_layer_ids
      .filter((lid) => !!findLayer(lid))
      .map((lid) => ensureLayerOnMap(lid));
    await Promise.allSettled(ensure);

    const desired = new Set(bookmark.visible_layer_ids);
    map.layers.forEach((l) => {
      if (l.id.startsWith('__rcmc_')) return;
      if (findLayer(l.id)) {
        setLayerVisible(l.id, desired.has(l.id));
      }
    });

    const ext = new Extent({
      xmin: bookmark.extent.xmin,
      ymin: bookmark.extent.ymin,
      xmax: bookmark.extent.xmax,
      ymax: bookmark.extent.ymax,
      spatialReference: new SpatialReference({ wkid: bookmark.extent.wkid ?? 4326 }),
    });
    await view.goTo(ext);

    return { id: bookmark.id, name: bookmark.name };
  },
};
